import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { ArrowLeft, Video, ExternalLink, Loader2 } from 'lucide-react';

/**
 * Embedded Jitsi Meet video session. Therapist + patient both navigate to
 * /admin/appointments/:appointment_id/telehealth or /patient/appointments/:appointment_id/telehealth
 * → same deterministic room name → both land in the same call.
 *
 * No API key needed — uses meet.jit.si public server.
 */
export default function Telehealth() {
  const { appointment_id } = useParams();
  const [room, setRoom] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    if (!appointment_id) return;
    api.get(`/appointments/${appointment_id}/telehealth-room`)
      .then(r => setRoom(r.data))
      .catch(e => setErr(e.response?.data?.detail || 'Failed to load video room'));
  }, [appointment_id]);

  if (err) return (
    <div className="p-8 max-w-3xl">
      <div className="bento-card text-center py-12">
        <Video className="w-10 h-10 text-[#B83A3A] mx-auto mb-3"/>
        <div className="text-sm text-fg-secondary mb-4">{err}</div>
        <Link to="/admin/appointments" className="btn-secondary inline-flex items-center gap-1.5 text-sm">
          <ArrowLeft className="w-4 h-4"/> Back
        </Link>
      </div>
    </div>
  );
  if (!room) return (
    <div className="p-8 max-w-3xl flex items-center gap-2 text-fg-muted">
      <Loader2 className="w-4 h-4 animate-spin"/> Loading video room…
    </div>
  );

  // Build a parameterized Jitsi URL with the participant name + skip pre-join UI.
  const params = new URLSearchParams({
    'userInfo.displayName': room.display_name || 'Participant',
  }).toString();
  const embedSrc = `${room.url}#config.prejoinPageEnabled=false&${params}`;

  return (
    <div className="p-4 md:p-6 max-w-7xl">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-brand font-semibold mb-1">TELEHEALTH</div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold tracking-tight">Video session</h1>
          <div className="text-xs text-fg-muted mt-1">Room: <span className="font-mono">{room.room}</span> · Powered by Jitsi Meet</div>
        </div>
        <div className="flex gap-2">
          <a href={room.url} target="_blank" rel="noopener noreferrer" className="btn-secondary text-sm inline-flex items-center gap-1.5" data-testid="telehealth-open-tab">
            <ExternalLink className="w-3.5 h-3.5"/> Open in new tab
          </a>
        </div>
      </div>

      <div className="rounded-xl overflow-hidden border border-soft bg-black" style={{ aspectRatio: '16 / 9' }} data-testid="telehealth-iframe">
        <iframe
          src={embedSrc}
          title="Telehealth video session"
          allow="camera; microphone; fullscreen; display-capture; autoplay"
          className="w-full h-full"
          style={{ border: 0 }}
        />
      </div>

      <div className="mt-4 surface border border-soft rounded-lg p-3 text-xs text-fg-secondary">
        <strong className="text-fg">Tips:</strong> Best in Chrome / Firefox. Allow microphone + camera when prompted.
        For HIPAA-grade encryption beyond the free Jitsi server, the admin can wire a self-hosted Jitsi or 8x8.vc paid account.
      </div>
    </div>
  );
}
